import { useState } from "react";
import { useNavigate } from "react-router";
import { createCashAdvanceDraft, submitRequest, uploadAttachment } from "../api/requests";
import { ApiError, type AdvanceLineInput } from "../api/types";

/**
 * DEL-AC-FRM-003 Rev 05 — Cash Advance, To Be Justified.
 *
 * Six rows, not eleven. The expense form has eleven and the difference is
 * visible at a glance, which is part of what makes each form recognisable to
 * someone who has filled them for years. See docs/13.
 */
const ROW_COUNT = 6;

/**
 * A row as typed: one amount, in naira, with optional decimals.
 *
 * THIS WAS TWO BOXES UNTIL 24 AUG 2026, AND IT COST REAL MONEY
 * -----------------------------------------------------------
 * The printed DEL-AC-FRM-003 splits its amount column ₦ / k, so this form did
 * too — faithfully, and wrongly. On paper the split is unmistakable because
 * the columns are ruled and labelled down the page. On screen it is a narrow
 * box and a narrower box under two one-character headings, and people read the
 * first as a quantity.
 *
 * Cost Control raised an advance for ₦20,200 of office consumables:
 *
 *     Water      ₦ 2   k 4200      read as 2 + 42.00  = ₦44.00
 *     Groundnut  ₦ 3   k 3000      read as 3 + 30.00  = ₦33.00
 *     Malt       ₦ 2   k 13000     read as 2 + 130.00 = ₦132.00
 *                                            Total    = ₦209.00
 *
 * The arithmetic was correct throughout. The form was wrong, and it was wrong
 * silently: nothing rejected 13,000 kobo, a quantity that does not exist. The
 * kobo input carried max="99" and had done since it was written, but `max` on
 * a number input only participates in native form validation, which this form
 * bypasses with its own submit handler. A bound that was declared and never
 * enforced — which is this project's most repeated finding, here costing two
 * orders of magnitude on a real request.
 *
 * One box removes the class of error rather than guarding it, and matches
 * DEL-AC-FRM-002's own screen, which has had a single NGN amount column all
 * along. Storage was decimal(18,2) either way; nothing about the data changes.
 */
type Row = {
  description: string;
  amount: string;
};

const emptyRow = (): Row => ({ description: "", amount: "" });

const money = (value: number) =>
  new Intl.NumberFormat("en-NG", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(
    value,
  );

/** Blank is zero, not invalid — eleven rows are printed and most stay empty. */
function rowAmount(row: Row): number {
  return Number(row.amount) || 0;
}

export function NewCashAdvance() {
  const navigate = useNavigate();

  const [rows, setRows] = useState<Row[]>(() =>
    Array.from({ length: ROW_COUNT }, emptyRow),
  );
  const [purpose, setPurpose] = useState("");

  // Who is going to collect the cash. Typed, not chosen: a driver or a site
  // supervisor need not exist anywhere in this system to be handed money, and
  // requiring them to would make the field useless for the case it is for.
  // The advance stays the requester's liability regardless of what goes here.
  const [beneficiaryName, setBeneficiaryName] = useState("");
  const [allocationType, setAllocationType] = useState<"Project" | "CostCentre">("CostCentre");
  const [projectCode, setProjectCode] = useState("");
  const [costCentreCode, setCostCentreCode] = useState("");
  const [stationScope, setStationScope] = useState<"InStation" | "OutOfStation">("InStation");
  const [hasSupportingDocuments, setHasSupportingDocuments] = useState(false);

  // The supporting document itself, not a claim that one exists.
  //
  // Workflow version 7 guards SUBMIT on AttachmentCount > 0, counted from
  // the Attachments table. hasSupportingDocuments above is the printed
  // form's tick box: it records what the requester said about themselves
  // and no guard has ever read it.
  const [supportingDocument, setSupportingDocument] = useState<File | null>(null);

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const total = rows.reduce((sum, row) => sum + rowAmount(row), 0);
  const filled = rows.filter((row) => row.description.trim().length > 0 && rowAmount(row) > 0);

  const allocationCode = allocationType === "Project" ? projectCode : costCentreCode;
  const canSubmit =
    filled.length > 0 &&
    purpose.trim().length > 0 &&
    allocationCode.trim().length > 0 &&
    supportingDocument !== null &&
    !busy;

  function updateRow(index: number, patch: Partial<Row>) {
    setRows((current) => current.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  }

  async function save() {
    setBusy(true);
    setError(null);

    try {
      const lines: AdvanceLineInput[] = filled.map((row) => ({
        description: row.description.trim(),
        currencyCode: "NGN",
        amount: rowAmount(row),
        // No FX rates feed exists, so a naira line carries a rate of 1 rather
        // than a number this screen invented. Same reasoning as the expense
        // form's untouched FX column.
        fxRate: 1,
        fxRateDate: new Date().toISOString().slice(0, 10),
      }));

      const created = await createCashAdvanceDraft({
        purpose: purpose.trim(),
        ...(beneficiaryName.trim() ? { beneficiaryName: beneficiaryName.trim() } : {}),
        allocationType,
        ...(allocationType === "Project"
          ? { projectCode: projectCode.trim() }
          : { costCentreCode: costCentreCode.trim() }),
        stationScope,
        hasSupportingDocuments,
        lines,
      });

      // Attach, then submit. One click for the person, three calls
      // underneath, because an attachment needs a request to belong to and
      // the guard needs the attachment to already be there.
      //
      // If the upload fails the draft still exists, so this navigates to it
      // either way rather than leaving the work stranded behind an error
      // message on a form the person is about to close.
      try {
        await uploadAttachment(created.requestId, supportingDocument!);
        await submitRequest(created.requestId);
      } finally {
        navigate(`/requests/${created.requestId}`);
      }
    } catch (e) {
      // Guard refusals arrive as ProblemDetails with a sentence written for a
      // person — most likely here "retire any overdue advance before
      // requesting another", which names exactly what to do.
      setError(e instanceof ApiError ? e.message : (e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-6">
      <header className="rounded border border-gray-200 bg-white p-4">
        <h1 className="text-lg font-semibold text-gray-900">Cash Advance — To Be Justified</h1>
        <p className="mt-1 text-sm text-gray-600">DEL-AC-FRM-003 Rev 05</p>
      </header>

      {error && (
        <p role="alert" className="rounded bg-red-50 p-4 text-red-800">
          {error}
        </p>
      )}

      <section className="rounded border border-gray-200 bg-white p-4">
        <label className="block text-sm text-gray-700" htmlFor="purpose">
          Please approve a Cash Advance for the underlisted expense(s)
        </label>
        <input
          id="purpose"
          value={purpose}
          onChange={(e) => setPurpose(e.target.value)}
          placeholder="Purpose of this advance"
          className="mt-1 min-h-11 w-full rounded border border-gray-300 p-2 text-sm focus:border-blue-600 focus:outline-none focus:ring-1 focus:ring-blue-600"
        />

        {/* Who collects the cash.

            Typed, not picked from a list, and that is the requirement rather
            than a shortcut: the person handed the money is often a driver or a
            site supervisor who exists in no system here. The expense claim's
            beneficiary is a payment target and needs an account behind it;
            this is a name so Treasury knows who is standing in front of them.

            Optional, because the ordinary case is that the requester collects
            it themselves — and whoever collects it, the advance stays the
            requester's to retire. */}
        <label className="mt-4 block text-sm text-gray-700" htmlFor="advance-beneficiary">
          Name of the Beneficiary
        </label>
        <input
          id="advance-beneficiary"
          value={beneficiaryName}
          onChange={(e) => setBeneficiaryName(e.target.value)}
          placeholder="Who will collect the cash — leave blank if it is you"
          className="mt-1 min-h-11 w-full rounded border border-gray-300 p-2 text-sm focus:border-blue-600 focus:outline-none focus:ring-1 focus:ring-blue-600"
        />
        <p className="mt-1 text-xs text-gray-500">
          Any name. This advance stays yours to retire whoever collects it, and bank details,
          if the money is to be transferred rather than handed over, are arranged with Treasury.
        </p>

        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 text-left text-xs uppercase text-gray-500">
                <th className="py-2 pr-2 font-medium">S/n</th>
                <th className="py-2 pr-2 font-medium">Description</th>
                <th className="py-2 pl-2 text-right font-medium">Amount (₦)</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, index) => (
                <tr key={index} className="border-b border-gray-100">
                  <td className="py-1 pr-2 text-gray-500">{index + 1}</td>
                  <td className="py-1 pr-2">
                    <input
                      aria-label={`Description, row ${index + 1}`}
                      value={row.description}
                      onChange={(e) => updateRow(index, { description: e.target.value })}
                      className="min-h-11 w-full rounded border border-gray-300 p-2 text-sm"
                    />
                  </td>
                  <td className="py-1 pl-2">
                    {/* One box, in naira, kobo after the point. step="0.01"
                        rather than "1" so 4200.50 is typeable; blank is zero. */}
                    <input
                      aria-label={`Amount in naira, row ${index + 1}`}
                      type="number"
                      inputMode="decimal"
                      min="0"
                      step="0.01"
                      placeholder="0.00"
                      value={row.amount}
                      // A focused number input changes value on scroll. On a
                      // form six rows long that is a silent edit to a money
                      // field made by scrolling past it.
                      onWheel={(e) => e.currentTarget.blur()}
                      onChange={(e) => updateRow(index, { amount: e.target.value })}
                      className="min-h-11 w-40 rounded border border-gray-300 p-2 text-right text-sm tabular-nums"
                    />
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={2} className="pt-3 text-right text-sm font-medium text-gray-700">
                  Total
                </td>
                <td className="pt-3 pl-2 text-right text-sm font-medium tabular-nums">
                  ₦{money(total)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </section>

      <section className="rounded border border-gray-200 bg-white p-4">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">
          Please tick as appropriate
        </h2>

        {/* Allocation is form-level here, unlike the expense form where it is
            per line. Radio rather than checkbox: the paper form's two boxes
            are alternatives, and a form that allowed both would be capturing
            something the process has no meaning for.

            THE CODE BOXES USED TO BE DISABLED UNTIL THEIR RADIO WAS PICKED
            --------------------------------------------------------------
            Reported by Cost Control on 24 August 2026: "The system was not
            giving the flexibility in inputting the project Code or Cost
            Centre."

            It was not a restriction anyone designed. A disabled input does not
            receive clicks, so clicking the very box you want to type in did
            nothing at all -- no focus, no cursor, no message. You had to find
            and hit the small radio circle first, and nothing on screen said
            so. The form looked like it was refusing you.

            Now both boxes stay enabled and typing in one selects it. That is
            how the paper form works: you write in the box you mean, and the
            tick follows. The radios remain, because the two options are still
            alternatives and only the selected code is submitted. */}
        <div className="mt-3 space-y-3">
          <label className="flex items-center gap-3 text-sm text-gray-800">
            <input
              type="radio"
              name="allocation"
              checked={allocationType === "Project"}
              onChange={() => setAllocationType("Project")}
              className="h-4 w-4"
            />
            Projects Specific
            <input
              aria-label="Project code"
              value={projectCode}
              onChange={(e) => setProjectCode(e.target.value)}
              onFocus={() => setAllocationType("Project")}
              placeholder="Project code"
              className={`min-h-11 w-56 rounded border p-2 text-sm ${
                allocationType === "Project"
                  ? "border-gray-300"
                  : "border-gray-200 text-gray-500"
              }`}
            />
          </label>

          <label className="flex items-center gap-3 text-sm text-gray-800">
            <input
              type="radio"
              name="allocation"
              checked={allocationType === "CostCentre"}
              onChange={() => setAllocationType("CostCentre")}
              className="h-4 w-4"
            />
            Non Projects Specific
            <input
              aria-label="Cost centre code"
              value={costCentreCode}
              onChange={(e) => setCostCentreCode(e.target.value)}
              onFocus={() => setAllocationType("CostCentre")}
              placeholder="Cost centre code"
              className={`min-h-11 w-56 rounded border p-2 text-sm ${
                allocationType === "CostCentre"
                  ? "border-gray-300"
                  : "border-gray-200 text-gray-500"
              }`}
            />
          </label>
        </div>

        <div className="mt-4 rounded border border-gray-200 bg-gray-50 p-3">
          <p className="text-sm font-medium text-gray-800">Supporting document</p>
          <p className="mt-1 text-xs text-gray-600">
            A quotation, pro-forma invoice or written request. Required — this is money
            not yet spent, so this is the only evidence an approver has.
          </p>
          <input
            type="file"
            aria-label="Supporting document"
            accept=".pdf,image/*"
            onChange={(e) => setSupportingDocument(e.target.files?.[0] ?? null)}
            className="mt-2 block w-full text-sm"
          />
          <p className="mt-1 text-xs text-gray-500">PDF or photograph, up to 10 MB.</p>
        </div>

        <label className="mt-4 flex items-center gap-3 text-sm text-gray-800">
          <input
            type="checkbox"
            checked={hasSupportingDocuments}
            onChange={(e) => setHasSupportingDocuments(e.target.checked)}
            className="h-4 w-4"
          />
          Attached documentation
        </label>
      </section>

      <section className="rounded border border-gray-200 bg-white p-4">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">
          Where is this being spent?
        </h2>

        {/* Not on the paper form as a tick box, but it sets the retirement
            deadline, and the footer states both windows. Asking here is
            honest; deriving it from somewhere else would be a guess about
            somebody's liability. */}
        <p className="mt-1 text-sm text-gray-600">
          This sets your retirement deadline: 24 working hours in station, 72 out of station.
          The advance is your liability until it is retired.
        </p>

        <div className="mt-3 space-y-2">
          {(
            [
              ["InStation", "Within local station state — retire within 24 working hours"],
              ["OutOfStation", "Out of station state — retire within 72 working hours"],
            ] as const
          ).map(([value, label]) => (
            <label key={value} className="flex items-center gap-3 text-sm text-gray-800">
              <input
                type="radio"
                name="station"
                checked={stationScope === value}
                onChange={() => setStationScope(value)}
                className="h-4 w-4"
              />
              {label}
            </label>
          ))}
        </div>
      </section>

      {/* Signatures are not typed here, for the same reason as the expense
          form: each one is recorded when its approver acts, and a typed name
          would be a claim nobody made. */}
      <section className="rounded border border-gray-200 bg-gray-50 p-4">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">
          Requested by · Verified by · Approved by
        </h2>
        <p className="mt-2 text-sm text-gray-600">
          Signatures are recorded automatically as each approver acts. Nothing here is typed.
        </p>
      </section>

      <div className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          disabled={!canSubmit}
          onClick={() => void save()}
          className="min-h-11 rounded bg-blue-700 px-4 py-2 font-medium text-white hover:bg-blue-800 disabled:opacity-50"
        >
          Save and submit
        </button>
        <span className="text-sm text-gray-600">
          {filled.length === 0
            ? "Add at least one line with a description and an amount."
            : supportingDocument === null
              ? "Attach the quotation or invoice supporting this advance."
              : `${filled.length} line${filled.length === 1 ? "" : "s"} · ₦${money(total)}`}
        </span>
      </div>
    </div>
  );
}
